/**
 * NAUI Freediver course requirements, from the NAUI Freediver Student Manual
 * (2021), "Course Sessions and Skills", p.48, plus the course objective on p.8.
 *
 * Purpose in this app: a self-assessment checklist and a set of training targets.
 * Ticking an item here is a personal note, NOT a certification — only a NAUI
 * instructor can sign off these skills. The two dry-trainable targets (a 1:30
 * static and the knowledge topics) are the ones this app can actually help with;
 * everything else is listed so the user knows what the course involves.
 */

export type SkillEnvironment = 'knowledge' | 'confined' | 'openWater';

export interface NauiSkill {
  id: string;
  environment: SkillEnvironment;
  group: string;
  label: string;
  /** True when this app's dry training directly develops the skill. */
  dryTrainable?: boolean;
}

export const NAUI_FREEDIVER_SKILLS: NauiSkill[] = [
  // Knowledge development
  { id: 'k-safety', environment: 'knowledge', group: 'Knowledge development', label: 'Safety & problem management', dryTrainable: true },
  { id: 'k-breathing', environment: 'knowledge', group: 'Knowledge development', label: 'Breathing & anxiety in freediving', dryTrainable: true },
  { id: 'k-equipment', environment: 'knowledge', group: 'Knowledge development', label: 'Equipment for freediving' },
  { id: 'k-physics', environment: 'knowledge', group: 'Knowledge development', label: 'Physics & physiology of freediving', dryTrainable: true },

  // Confined water — watermanship
  { id: 'c-swim-100', environment: 'confined', group: 'Watermanship & stamina', label: '100 m continuous swim without fins, snorkel, wetsuit or flotation' },
  { id: 'c-tread-5', environment: 'confined', group: 'Watermanship & stamina', label: 'Tread water for a minimum of 5 minutes' },

  // Confined water — preparation and entry
  { id: 'c-prep', environment: 'confined', group: 'Preparation & entry', label: 'Prepare freediving equipment without instructor assistance' },
  { id: 'c-entry', environment: 'confined', group: 'Preparation & entry', label: 'Proper entry procedure' },

  // Confined water — snorkel
  { id: 'c-snorkel-blast', environment: 'confined', group: 'Snorkel skills', label: 'Snorkel blast 5 times' },
  { id: 'c-snorkel-25', environment: 'confined', group: 'Snorkel skills', label: '25 m no-mask snorkel swim without surfacing' },

  // Confined water — finning
  { id: 'c-flutter', environment: 'confined', group: 'Proper fin use', label: 'Flutter kick against the wall' },
  { id: 'c-scull', environment: 'confined', group: 'Proper fin use', label: 'Stationary position / sculling' },
  { id: 'c-upright', environment: 'confined', group: 'Proper fin use', label: 'Upright finning' },

  // Confined water — buoyancy
  { id: 'c-belt-replace', environment: 'confined', group: 'Buoyancy', label: 'Weight belt removal and replacement' },
  { id: 'c-belt-ascent', environment: 'confined', group: 'Buoyancy', label: 'Weight belt removal and ascent' },
  { id: 'c-neutral', environment: 'confined', group: 'Buoyancy', label: 'Neutral buoyancy surface determination (rule of thumb)' },
  { id: 'c-exhale-test', environment: 'confined', group: 'Buoyancy', label: 'Buoyancy exhalation test' },

  // Confined water — mask
  { id: 'c-mask-ascent', environment: 'confined', group: 'Mask skills', label: 'Flooded mask ascent' },
  { id: 'c-mask-flood', environment: 'confined', group: 'Mask skills', label: 'Shallow water full flood' },
  { id: 'c-mask-depth', environment: 'confined', group: 'Mask skills', label: 'Flood at depth' },

  // Confined water — recovery and rescue
  { id: 'c-recovery', environment: 'confined', group: 'Recovery & rescue', label: 'Recovery breathing and coaching, as recovering diver and as surface safety', dryTrainable: true },
  { id: 'c-lmc', environment: 'confined', group: 'Recovery & rescue', label: 'LMC at surface (rescue scenario)' },
  { id: 'c-blackout-surface', environment: 'confined', group: 'Recovery & rescue', label: 'Blackout at surface (rescue scenario)' },
  { id: 'c-blackout-uw', environment: 'confined', group: 'Recovery & rescue', label: 'Underwater assistance with blackout' },

  // Confined water — apnea
  { id: 'c-static-4', environment: 'confined', group: 'Static apnea', label: 'Minimum of 4 consecutive static breath-holds, as performer and safety' },
  { id: 'c-static-90', environment: 'confined', group: 'Static apnea', label: 'Complete a minimum 1:30 static apnea without any hypoxic symptoms', dryTrainable: true },
  { id: 'c-dynamic-3', environment: 'confined', group: 'Dynamic apnea', label: 'Minimum of 3 dynamic performances, as performer and safety' },
  { id: 'c-dynamic-25', environment: 'confined', group: 'Dynamic apnea', label: 'Complete a minimum 25 m dynamic apnea' },

  // Open water
  { id: 'o-recovery', environment: 'openWater', group: 'Open water skills', label: 'Recovery breathing and coaching', dryTrainable: true },
  { id: 'o-neutral-surface', environment: 'openWater', group: 'Open water skills', label: 'Neutral buoyancy surface determination (rule of thumb)' },
  { id: 'o-positive-5', environment: 'openWater', group: 'Open water skills', label: 'Positive buoyancy at 5 m / 16 ft' },
  { id: 'o-neutral-10', environment: 'openWater', group: 'Open water skills', label: 'Neutral buoyancy at 10 m / 33 ft' },
  { id: 'o-exhale-test', environment: 'openWater', group: 'Open water skills', label: 'Buoyancy exhalation test at surface' },
  { id: 'o-belt-surface', environment: 'openWater', group: 'Open water skills', label: 'Weight belt removal & replacement at surface' },
  { id: 'o-fi-dives', environment: 'openWater', group: 'Open water skills', label: 'Four free immersion warm-up dives reaching minimum 10 m / 33 ft' },
  { id: 'o-mask-ascent', environment: 'openWater', group: 'Open water skills', label: 'Flooded mask ascent from 5 m / 16 ft' },
  { id: 'o-belt-ascent', environment: 'openWater', group: 'Open water skills', label: 'Weight belt removal and ascent from 5 m / 16 ft' },
  { id: 'o-cb-dives', environment: 'openWater', group: 'Open water skills', label: 'Six target constant ballast dives reaching minimum 10 m / 33 ft' },
  { id: 'o-lmc', environment: 'openWater', group: 'Emergency rescue', label: 'LMC at surface (rescue scenario)' },
  { id: 'o-blackout-surface', environment: 'openWater', group: 'Emergency rescue', label: 'Blackout at surface (rescue scenario)' },
  { id: 'o-blackout-uw', environment: 'openWater', group: 'Emergency rescue', label: 'Underwater assistance with blackout' },
];

/** Course objective, p.8. These are the numeric targets worth tracking against. */
export const NAUI_FREEDIVER_TARGETS = {
  maxDepthMeters: 20,
  maxDepthFeet: 66,
  minStaticSeconds: 90,
  minDynamicMeters: 25,
  minDynamicFeet: 82,
} as const;

export const NAUI_GRADUATION_REQUIREMENTS: string[] = [
  'Complete all knowledge development, confined water and open water training sessions.',
  'Demonstrate safe and responsible freediving practices.',
  'Achieve a passing score of 80% on the final exam.',
];

export const NAUI_PREREQUISITES: string[] = [
  'At least 16 years of age (10 for Jr. Basic Freediver).',
  'Be a competent swimmer.',
  'Be a NAUI Snorkeler or have an equivalent skill level.',
];

export const NAUI_EXAM_PASS_PERCENT = 80;

/**
 * Open water training is not required for a Pool-Only certification, so the
 * open-water items can legitimately stay unticked.
 */
export const POOL_ONLY_NOTE =
  'Open water training is not required for Pool-Only certification. Dynamic apnea '
  + 'is required for students seeking Pool-Only certification.';

export const SKILLS_DISCLAIMER =
  'This checklist is for your own tracking only. It is not a certification — these '
  + 'skills must be demonstrated to and signed off by a NAUI instructor. Rescue '
  + 'skills in particular cannot be learned from an app.';

export const skillsByEnvironment = (environment: SkillEnvironment): NauiSkill[] =>
  NAUI_FREEDIVER_SKILLS.filter(s => s.environment === environment);

export const dryTrainableSkills = (): NauiSkill[] =>
  NAUI_FREEDIVER_SKILLS.filter(s => s.dryTrainable === true);

/** Distinct group names, in declaration order, for rendering sections. */
export const skillGroups = (environment: SkillEnvironment): string[] =>
  Array.from(new Set(skillsByEnvironment(environment).map(s => s.group)));
